import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/cn';
import { EVENTO_FICHA, EVENTO_NAVEGO } from '@/lib/eventos';
import { Button } from '@/ui/Button';
import { registrarVisita } from './accesos';
import { useAvisoNativo, useFichaPendiente, useSinLeer } from './api';
import { BusquedaGlobal } from './BusquedaGlobal';
import { MenuLateral } from './MenuLateral';
import { Notificaciones } from './Notificaciones';
import { puedeVer, TODOS } from './navegacion';

/**
 * El cromo: lo que rodea a cada pantalla y no cambia al navegar.
 *
 * ## Una sola raíz, montada una vez
 *
 * `app.js` conserva el router y sigue montando cada pantalla en
 * `#page-container`. Esto es una raíz aparte que se monta al entrar y **no se
 * desmonta al navegar**, y por eso no es «una raíz incrustada» de las que el
 * portal cerró el 2026-09-18: el peligro de aquellas era que el vanilla
 * repintaba su contenedor con `innerHTML` y dejaba la raíz huérfana con sus
 * suscripciones vivas. Nada repinta el cromo.
 *
 * Sí se desmonta al salir, y eso no es un detalle: la campana pregunta cada
 * minuto, y ante un 401 `api.js` llama a `showLogin()`. Con el cromo vivo detrás
 * de la pantalla de acceso, ese latido seguiría pidiendo, seguiría recibiendo
 * 401 y seguiría llamando a `showLogin()` cada minuto para siempre. Por eso
 * `_mostrarAcceso()` lo desmonta.
 *
 * ## Lo que necesita del router
 *
 * Solo **qué pantalla se está viendo**, para marcar el destino activo y poner el
 * título. Llega por el evento `EVENTO_NAVEGO`; con un dato compartido entre dos
 * raíces haría falta un estado global, que es justo lo que complica tener dos.
 */
export function Cromo() {
  const [pagina, setPagina] = useState(() => window._paginaActual || 'dashboard');
  const [hash, setHash] = useState(() => location.hash.slice(1) || 'dashboard');
  const [titulo, setTitulo] = useState(() => window._tituloActual || '');
  const [menuAbierto, setMenuAbierto] = useState(false);

  // El aviso del sistema operativo. Cuelga del cromo y no de la campana para
  // que siga funcionando con el panel cerrado, que es el caso normal.
  useAvisoNativo(useSinLeer().data?.unread);

  useEffect(() => {
    const alNavegar = (e: Event) => {
      const d = (e as CustomEvent<{ pagina: string; hash: string; titulo: string }>).detail;
      if (!d) return;
      setPagina(d.pagina);
      setHash(d.hash);
      setTitulo(d.titulo);
      setMenuAbierto(false);   // en móvil, navegar cierra el cajón
      // «Recientes» se apunta aquí y no en `app.js`: era `trackRecent()`, y lo
      // que necesita —la etiqueta y el icono del destino— vive en esta capa.
      // Síncrono a propósito: la tarjeta de Inicio lee `localStorage` al
      // montarse, y el router monta la pantalla justo después de emitir esto.
      registrarVisita(d.pagina, d.hash);
    };
    window.addEventListener(EVENTO_NAVEGO, alNavegar);
    return () => window.removeEventListener(EVENTO_NAVEGO, alNavegar);
  }, []);

  return (
    <>
      <MenuLateral
        paginaActiva={pagina}
        hashActivo={hash}
        abierto={menuAbierto}
        onCerrar={() => setMenuAbierto(false)}
      />

      <header className="sticky top-0 z-armazon flex h-[var(--topbar-h)] items-center gap-2 border-b border-line bg-surface px-3">
        <button
          type="button"
          className="flex size-9 shrink-0 items-center justify-center rounded-sm text-5 text-ink-2 hover:bg-sunken focus-visible:focus-anillo menu:hidden"
          onClick={() => setMenuAbierto(true)}
          aria-label="Abrir el menú"
          aria-expanded={menuAbierto}
        >
          <i className="bi bi-list" aria-hidden="true" />
        </button>

        {/* El título se esconde en estrecho para dejarle el ancho a la búsqueda,
            igual que antes. No desaparece del documento: sigue siendo el <h1> de
            la pantalla, que es lo que ordena el índice de un lector. */}
        <h1 className="min-w-0 flex-1 truncate text-4 font-bold text-brand-ink max-md:sr-only">
          {titulo}
        </h1>

        <BusquedaGlobal />

        <div className="flex shrink-0 items-center gap-1">
          <BotonDeIcono
            icono="bi-question-circle"
            etiqueta="¿Cómo funciona?"
            onClick={() => window.Onboarding?.open?.(true)}
          />
          <BotonDeIcono
            icono="bi-palette"
            etiqueta="Apariencia: claro, oscuro o personalizar"
            onClick={() => window.Theme?.openPanel?.()}
          />
          <Notificaciones />
          <a
            href="#perfil"
            className="flex size-9 items-center justify-center rounded-sm text-5 text-ink-2 no-underline hover:bg-sunken hover:text-brand-ink focus-visible:focus-anillo"
            title="Mi perfil"
          >
            <i className="bi bi-person-circle" aria-hidden="true" />
            <span className="sr-only">Mi perfil</span>
          </a>
        </div>
      </header>

      <AvisoDeFicha />
      <NavegacionInferior paginaActiva={pagina} />
    </>
  );
}

function BotonDeIcono({ icono, etiqueta, onClick }: {
  icono: string;
  etiqueta: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="flex size-9 items-center justify-center rounded-sm text-5 text-ink-2 hover:bg-sunken hover:text-brand-ink focus-visible:focus-anillo"
      onClick={onClick}
      // `title` Y nombre accesible: el `title` solo lo ve quien usa ratón.
      title={etiqueta}
      aria-label={etiqueta}
    >
      <i className={`bi ${icono}`} aria-hidden="true" />
    </button>
  );
}

/** Los cinco atajos de abajo en móvil. Las páginas son las mismas de siempre;
 *  la etiqueta corta y el icono también, porque ahí solo caben cinco y no son
 *  los del menú. */
const ABAJO = [
  { page: 'dashboard', hash: 'dashboard', icono: 'bi-house-door', etiqueta: 'Inicio' },
  { page: 'feed', hash: 'novedades', icono: 'bi-chat-square-text', etiqueta: 'Feed' },
  { page: 'courses', hash: 'cursos', icono: 'bi-play-circle', etiqueta: 'Cursos' },
  { page: 'celebrations', hash: 'celebraciones', icono: 'bi-balloon-heart', etiqueta: 'Celebra' },
  { page: 'profile', hash: 'perfil', icono: 'bi-person-circle', etiqueta: 'Perfil' },
];

/**
 * La barra inferior de móvil.
 *
 * El marcado declaraba `data-module` y `app.js` avisaba con un toast al pulsar
 * algo sin acceso — enseñaba la puerta y después la cerraba. Aquí el requisito
 * sale de `navegacion.ts` por la misma página, así que lo que no se alcanza **no
 * se pinta**: es el mismo criterio del menú lateral y del router.
 */
function NavegacionInferior({ paginaActiva }: { paginaActiva: string }) {
  const visibles = ABAJO.filter((a) => {
    const d = TODOS.find((x) => x.page === a.page);
    return !d || puedeVer(d);
  });

  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-armazon flex border-t border-line bg-surface menu:hidden"
      aria-label="Accesos rápidos"
    >
      {visibles.map((a) => {
        const esta = a.page === paginaActiva;
        return (
          <a
            key={a.page}
            href={`#${a.hash}`}
            aria-current={esta ? 'page' : undefined}
            className={cn(
              'flex flex-1 flex-col items-center gap-0.5 py-1.5 text-1 no-underline',
              esta ? 'font-semibold text-brand-ink' : 'text-muted',
            )}
          >
            <i className={`bi ${a.icono} text-4`} aria-hidden="true" />
            {a.etiqueta}
          </a>
        );
      })}
    </nav>
  );
}

const FICHA_DESCARTADA = 'fichaPendienteDescartada';

/**
 * «Te faltan datos en tu ficha».
 *
 * Vive en el cromo y no dentro de una pantalla porque tiene que verse se navegue
 * a donde se navegue: el filtro de datos iniciales solo dispara en
 * «preregistro», así que una columna que Bienestar agregue después no se le
 * pediría NUNCA a quien ya está activo.
 *
 * Se puede descartar, pero **recordando para qué campos**: si mañana aparece una
 * columna nueva, vuelve a salir. Descartar «ahora no» no puede convertirse en
 * «no me lo vuelvas a pedir nunca», y por eso lo que se guarda es la lista, no
 * un booleano.
 */
function AvisoDeFicha() {
  const qc = useQueryClient();
  const consulta = useFichaPendiente(true);
  const [descartadas, setDescartadas] = useState<string[]>(
    () => Utils.recordar.leer<string[]>(FICHA_DESCARTADA, []) || [],
  );

  // Guardar la ficha ocurre en la OTRA raíz, que tiene su propia caché: sin este
  // aviso el `invalidateQueries` de la pantalla de Perfil no llegaría aquí y el
  // aviso seguiría pidiendo datos que ya se acababan de entregar.
  useEffect(() => {
    const releer = () => void qc.invalidateQueries({ queryKey: ['armazon', 'ficha-pendiente'] });
    window.addEventListener(EVENTO_FICHA, releer);
    return () => window.removeEventListener(EVENTO_FICHA, releer);
  }, [qc]);

  const faltan = consulta.data?.pendientes ?? [];

  // Cuando ya no falta nada se olvida el descarte, igual que hacía
  // `_olvidarDescarte()`: el próximo hueco tiene que volver a avisar.
  useEffect(() => {
    if (consulta.data && faltan.length === 0 && descartadas.length) {
      Utils.recordar.olvidar(FICHA_DESCARTADA);
      setDescartadas([]);
    }
  }, [consulta.data, faltan.length, descartadas.length]);

  if (!faltan.length) return null;
  // Descartado para EXACTAMENTE estos: si aparece uno nuevo, vuelve entero.
  if (faltan.every((x) => descartadas.includes(x))) return null;

  return (
    <div className="mx-3 mt-3 flex flex-wrap items-center gap-3 rounded-sm border border-warn bg-warn/10 p-3">
      <i className="bi bi-person-vcard shrink-0 text-5 text-warn-ink" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <strong className="block text-3 text-brand-ink">
          {faltan.length === 1 ? 'Falta un dato tuyo' : `Faltan ${faltan.length} datos tuyos`}
        </strong>
        <span className="text-2 text-muted">{faltan.join(' · ')}</span>
      </div>
      <Button size="sm" onClick={() => window.APP?.navigate('perfil/ficha')}>
        <i className="bi bi-pencil" aria-hidden="true" /> Completar
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          Utils.recordar.guardar(FICHA_DESCARTADA, faltan);
          setDescartadas(faltan);
        }}
        title="Ocultar hasta que cambie"
      >
        <i className="bi bi-x-lg" aria-hidden="true" />
        <span className="sr-only">Ocultar este aviso hasta que cambien los datos que faltan</span>
      </Button>
    </div>
  );
}
